import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from '@remix-run/react';
import { useDebounce } from 'ahooks';
import {
    CONTACT_SUGGESTIONS_API_ROUTE,
    type loader,
} from '~/routes/remix-api.chat.contact-suggestions';
import type { ContactSuggestion } from '../types';

/**
 * Hook to get contact suggestions for the current user.
 * @param initialSearch The initial search value. Defaults to an empty string.
 * @param limit The initial limit of suggestions to return. Defaults to 100.
 */
export function useContactSuggestions({
    initialSearch = '',
    initialLimit = 100,
}: {
    initialSearch?: string;
    initialLimit?: number;
} = {}) {
    const isFetched = useRef(false);
    const [params, setParams] = useState({
        q: initialSearch,
        limit: initialLimit,
    });
    const debouncedParams = useDebounce(
        { ...params, q: params.q.trim() },
        { wait: 300 }
    );
    const fetcher = useFetcher<typeof loader>();
    const suggestions = useMemo<ContactSuggestion[]>(() => {
        return fetcher.data?.suggestions.nodes ?? [];
    }, [fetcher.data]);
    const pageInfo = useMemo(() => {
        return fetcher.data?.suggestions.pageInfo;
    }, [fetcher.data]);
    const apiUrl = useMemo(() => {
        const url = new URL(
            CONTACT_SUGGESTIONS_API_ROUTE,
            window.location.origin
        );
        url.searchParams.set('q', debouncedParams.q);
        url.searchParams.set('limit', String(debouncedParams.limit));
        return `${url.pathname}${url.search}`;
    }, [debouncedParams]);

    const setParamsCallback = useCallback(
        (newParams: Partial<typeof params>) => {
            setParams((prev) => ({
                ...prev,
                ...newParams,
            }));
        },
        []
    );

    useEffect(() => {
        if (!fetcher.data && fetcher.state === 'idle' && !isFetched.current) {
            // Fetch initial suggestions on mount.
            fetcher.load(apiUrl);
            isFetched.current = true;
        } else if (isFetched.current) {
            // Refetch when the query params change.
            fetcher.load(apiUrl);
        }
        // Must not include `fetcher` to avoid infinite loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl]);

    return {
        /**
         * The list of contact suggestions.
         */
        suggestions,
        /**
         * The pagination information for the contact suggestions query.
         */
        pageInfo,
        /**
         * The suggestions are currently being loaded.
         */
        isLoading: fetcher.state !== 'idle',
        /**
         * The current search value. This is used to filter the suggestions. The
         * actual values passed to the API is debounced to prevent calling the
         * API on every key press.
         */
        search: params.q,
        /**
         * Set the query params to apply to the suggestions API call. Changing the
         * params will trigger a call to the API after a short delay (debounced).
         */
        setParams: setParamsCallback,
    };
}
